import policyDocumentService, {
  DOCUMENT_TYPE_OPTIONS,
  MAX_POLICY_PDF_MB,
} from "../../../services/policyDocumentService";
import * as XLSX from "xlsx";

export { DOCUMENT_TYPE_OPTIONS, MAX_POLICY_PDF_MB };

export const SCOPES = {
  general: "general",
  customer: "customer",
};

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English (en)" },
  { value: "mm", label: "Myanmar (mm)" },
];

export function formatLanguageLabel(value) {
  const match = LANGUAGE_OPTIONS.find((option) => option.value === value);
  return match ? match.label : value || "—";
}

export function isValidScope(scope) {
  return scope === SCOPES.general || scope === SCOPES.customer;
}

export function validatePdfFile(file, errors, field = "file") {
  if (!file) {
    errors[field] = "PDF file is required.";
    return false;
  }
  const isPdf =
    file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
  if (!isPdf) {
    errors[field] = "Only PDF files are allowed.";
    return false;
  }
  const maxBytes = MAX_POLICY_PDF_MB * 1024 * 1024;
  if (file.size > maxBytes) {
    errors[field] = `PDF must be ${MAX_POLICY_PDF_MB}MB or smaller.`;
    return false;
  }
  return true;
}

export function validatePdfFiles(files, errors, field = "file") {
  const list = Array.isArray(files) ? files : [];
  if (!list.length) {
    errors[field] = "At least one PDF file is required.";
    return false;
  }
  for (const file of list) {
    const isPdf =
      file.type === "application/pdf" ||
      file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      errors[field] = `Only PDF files are allowed: ${file.name}`;
      return false;
    }
    const maxBytes = MAX_POLICY_PDF_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      errors[field] = `PDF must be ${MAX_POLICY_PDF_MB}MB or smaller: ${file.name}`;
      return false;
    }
  }
  return true;
}

export async function fetchAllDocuments(scope, token, params) {
  const exportParams = { ...params, all: "1" };
  return fetchDocuments(scope, token, exportParams);
}

export async function fetchDocuments(scope, token, params) {
  if (scope === SCOPES.customer) {
    const res = await policyDocumentService.getCustomerDocuments(token, params);
    return res.data;
  }
  const res = await policyDocumentService.getGeneralDocuments(token, params);
  return res.data;
}

export async function fetchDocument(scope, token, id) {
  if (scope === SCOPES.customer) {
    const res = await policyDocumentService.getCustomerDocument(token, id);
    return res.data;
  }
  const res = await policyDocumentService.getGeneralDocument(token, id);
  return res.data;
}

export async function deleteDocument(scope, token, id) {
  if (scope === SCOPES.customer) {
    return policyDocumentService.deleteCustomer(token, id);
  }
  return policyDocumentService.deleteGeneral(token, id);
}

export async function downloadDocument(scope, token, id) {
  if (scope === SCOPES.customer) {
    return policyDocumentService.downloadCustomer(token, id);
  }
  return policyDocumentService.downloadGeneral(token, id);
}

export function openPdfBlob(blob, fileName) {
  const url = window.URL.createObjectURL(
    new Blob([blob], { type: "application/pdf" })
  );
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return fileName;
}

export async function uploadDocument(scope, token, formData) {
  if (scope === SCOPES.customer) {
    return policyDocumentService.uploadCustomer(token, formData);
  }
  return policyDocumentService.uploadGeneral(token, formData);
}

export async function updateDocument(scope, token, id, formData) {
  if (scope === SCOPES.customer) {
    return policyDocumentService.updateCustomer(token, id, formData);
  }
  return policyDocumentService.updateGeneral(token, id, formData);
}

export async function loadProductTypes(token) {
  const res = await policyDocumentService.getProductTypes(token);
  return res.data?.productTypes || [];
}

export function resolveDocumentType(preset, customValue, useCustom) {
  return useCustom ? customValue.trim() : preset;
}

export function buildListFilterParams({
  scope,
  scopeFilter,
  documentTypeFilter,
  languageFilter,
  dateFrom,
  dateTo,
  limit,
  offset,
}) {
  const params = {};
  if (limit !== undefined) params.limit = limit;
  if (offset !== undefined) params.offset = offset;
  if (documentTypeFilter?.trim()) {
    params.document_type = documentTypeFilter.trim();
  }
  if (languageFilter?.trim()) {
    params.language = languageFilter.trim();
  }
  if (scope === SCOPES.general && scopeFilter?.trim()) {
    params.product_type = scopeFilter.trim();
  }
  if (scope === SCOPES.customer && scopeFilter?.trim()) {
    params.policy_no = scopeFilter.trim();
  }
  if (dateFrom) params.from = dateFrom;
  if (dateTo) params.to = dateTo;
  return params;
}

export function exportPolicyDocumentsToExcel(scope, rows, filenamePrefix) {
  const dataToExport =
    scope === SCOPES.general
      ? rows.map((row) => ({
          ID: row.id,
          ProductName: row.product_type,
          DocumentType: row.document_type,
          Language: row.language,
          FileName: row.file_name,
          CreatedDate: row.created_at || row.createdAt,
        }))
      : rows.map((row) => ({
          ID: row.id,
          PolicyNo: row.policy_no,
          DocumentType: row.document_type,
          Language: row.language,
          FileName: row.file_name,
          CreatedDate: row.created_at || row.createdAt,
        }));

  const sheet = XLSX.utils.json_to_sheet(dataToExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "PolicyDocuments");
  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `${filenamePrefix}-${date}.xlsx`);
}
